import { DateTime } from "luxon";

import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { IObstetricaLabResultsGroup, LabObservationObstetricalFile } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Patient } from "@/Common/Models/Patient/Patient";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { IExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FileTypeObstetricalFile } from "@/ExportableFile/Models/FileTypes/FileTypeObstetricalFile";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { PageOptionSelector } from "@/ExportableFile/Models/PageOptionSelector";
import { ObstetricalFileAction } from "@/Patient/Components/MedicalNote/Actions/ObstetricalFileAction";
import { FormContainer } from "@/Patient/Components/MedicalNote/Forms/FormContainer";
import { FormNavigation } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormNavigation";
import { FormPage } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormPage";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { FormDisplay } from "@/Patient/Models/Forms/FormDisplay";
import { FormExtensibleComponents } from "@/Patient/Models/Forms/FormExtendableComponentsEnum";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { ObstetricalCounters } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { BaseObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileForm";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";

interface IObstetricalFileProps {
    model: ObstetricalFileModel;
    contributionId: string;
    previousObstetricalFile: BaseObstetricalFileModel;
    patient: Patient;
    encounter: Encounter;
    formDisplay: FormDisplay;
    sentFaxes: SentFaxInfo[];
    obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary;
    consultationDateAtStartOfObstetricalFile: Date;
    fetchLabObservations: () => Promise<IObstetricaLabResultsGroup[]>
    isReadOnly?: boolean;
    isEditMode?: boolean;
    isNotInteractive?: boolean;
    obstetricalFileLabObservations?: KeyValuePair<string, LabObservationObstetricalFile[]>[];
    isFormExtended?: boolean;
    isSecondScreen?: boolean;

    onDelete: () => void;
    onFormDisplayChange: (formDisplay: FormDisplay) => void;
    onFormExtend: (formDisplay: FormDisplay) => void;
}

class ObstetricalFileState<TForm extends BaseObstetricalFileForm> {
    public currentPage: FormPage;
    public form: TForm;
    public formSnapshot: string;
    public isVitalSignSaving: boolean;
}

abstract class ObstetricalFile<TForm extends BaseObstetricalFileForm> extends BaseReactComponent<IObstetricalFileProps, ObstetricalFileState<TForm>> {
    protected obstetricalFileTypeCssClass: string = "";
    private readonly action: ObstetricalFileAction = new ObstetricalFileAction();
    private pageNavigation: FormNavigation;
    private handleFormUpdateThrottlingSeconds: number = 7;
    private throttleFunc?: number;
    private lastTimeHandleFormUpdateExecution: number;

    constructor(props: IObstetricalFileProps) {
        super(props);

        this.pageNavigation = this.loadFormNavigation(this.props);
        this.state = this.buildState(props);
    }

    public componentDidUpdate(prevProps: Readonly<IObstetricalFileProps>): void {
        if (this.props.encounter && this.props.encounter.Id !== prevProps.encounter.Id) {
            if (this.throttleFunc) {
                this.updateForm(this.state.form, null, null, false);
                this.removeThrottleUpdateForm();
            }

            this.pageNavigation = this.loadFormNavigation(this.props);
            this.setState(this.buildState(this.props));
        }

        // If the encounter it's changing from edit mode to view mode, then we force to save the form.
        // This is required since the debouncing can cause information lost when several changes are made fastly.
        if (!prevProps.isReadOnly && this.props.isReadOnly) {
            this.updateForm(this.state.form, null, null, false, true);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (this.throttleFunc) {
            this.updateForm(this.state.form, null, null, false);
        }
    }

    public async mutateStateAsync(mutation: (state: ObstetricalFileState<TForm>) => void): Promise<void> {
        return new Promise(resolve => this.mutateState(mutation, resolve));
    }

    public render(): JSX.Element {
        if (!this.state)
            return null;

        return (
            <FormContainer
                sentFaxes={this.props.sentFaxes}
                isSecondScreen={this.props.isSecondScreen}
                patient={this.props.patient}
                contributionId={this.props.contributionId}
                containerId={this.getComponentId()}
                header={this.pageNavigation.formPages.map(p => this.renderPageNumberHeader(p))}
                containerCssClass={`obstetrical-file ${this.obstetricalFileTypeCssClass}`}
                isExtended={this.props.isReadOnly ? this.props.isFormExtended : this.props.formDisplay.isExtended}
                onToggle={() => this.props.onFormExtend(this.props.formDisplay.toggleSection(FormExtensibleComponents.Form))}
                delete={{
                    show: this.props.model.IsLatestContribution && !this.props.isReadOnly && this.props.isEditMode,
                    onClick: this.props.onDelete
                }}
                print={{
                    disabled: this.state.isVitalSignSaving,
                    show: !this.props.isNotInteractive,
                    onClick: () => this.handleFormUpdate(this.state.form, () => this.handleOnPrint(), true)
                }}
                fax={{
                    disabled: this.state.isVitalSignSaving,
                    show: sessionStore.faxAccess && !this.props.isNotInteractive,
                    onClick: () => this.handleFormUpdate(this.state.form, () => this.dispatchFaxOrPrint(this.initFileTypeObstetricalFile(), ExportMode.Fax), true)
                }}
                portal={{
                    disabled: this.state.isVitalSignSaving,
                    show: (this.props.patient.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) && !this.props.isNotInteractive,
                    onClick: () => this.handleFormUpdate(this.state.form, () => this.handleOnPortalSync(), true),
                    item: this.props.model
                }}
            >
                <div className="body">
                    {this.state.currentPage.render()}
                    {this.renderBottomPageNavigation()}
                </div>
            </FormContainer>
        );
    }

    private buildState(props: IObstetricalFileProps): ObstetricalFileState<TForm> {
        const state = new ObstetricalFileState<TForm>();
        state.currentPage = this.pageNavigation.getSelectedPage();
        state.form = props.model.getFormObject<TForm>();
        state.formSnapshot = props.model.Form;

        return state;
    }

    protected abstract getComponentId(): string;
    protected abstract loadFormNavigation(props: IObstetricalFileProps): FormNavigation;
    protected abstract getSelectionPage(): PageOptionSelector[];
    protected abstract getPageNumbers(): number[];

    protected onPageUnmount(): void {
        this.updateFormWithThrottling(true);
    }

    protected handleFormUpdate(form: TForm, then?: () => void, avoidThrottling: boolean = false): void {
        this.mutateState(
            s => s.form = form,
            async () => {
                await this.updateFormWithThrottling(avoidThrottling);
                then?.();
            }
        );
    }

    protected handleObstetricalCountersUpdate(form: TForm): void {
        this.mutateState(
            s => s.form = form,
            () => this.updateForm(this.state.form, this.props.model.IsLatest ? form.ObstetricalCounters : null)
        );
    }

    protected handleVitalSignUpdate(vital: ObstetricalFileVitalSign, emrTemplateId: string): Promise<MedicalItemVitalSign> {
        return new Promise(resolve => {
            if (!vital?.hasEnoughDataToSaveMedicalItem())
                resolve(null);
            else
                this.mutateState(
                    s => s.isVitalSignSaving = true,
                    () => this.updateVitalSign(vital, emrTemplateId).then(vitalSign => resolve(vitalSign))
                );
        });
    }

    protected handleObstetricalFileVitalSignLink(form: TForm, emrTemplateId: string): void {
        this.mutateState(
            s => s.form = form,
            () => this.updateForm(this.state.form, null, emrTemplateId)
        );
    }

    private renderPageNumberHeader(formPage: FormPage): JSX.Element {
        const selectedCssClass = this.pageNavigation.isSelectedPage(formPage) && this.props.formDisplay.isExtended ? " selected" : "";

        return (
            <div
                key={formPage.name}
                className={`nav-page ${selectedCssClass} ${this.state.isVitalSignSaving ? "disabled" : ""}`}
                onClick={e => {
                    e.stopPropagation();
                    if (!this.state.isVitalSignSaving) {
                        this.handlePageNumberClick(formPage);
                    }
                }}
            >
                <a className={selectedCssClass}>{formPage.name}</a>
            </div>
        );
    }

    private renderBottomPageNavigation(): JSX.Element {
        const spacingClass = this.pageNavigation.isFirstPageSelected() ? "flex-end" : "flex-space-between";

        return (
            <div className={`flex-container nav-footer ${spacingClass}`}>
                {!this.pageNavigation.isFirstPageSelected() && (
                    <a
                        onClick={() => this.mutateState(
                            s => s.currentPage = this.pageNavigation.previousPage(),
                            () => {
                                this.updateFormDisplaySelectedPageId(this.pageNavigation.getSelectedPage().id);
                                this.scrollToTop();
                            }
                        )}
                    >
                        {cacheStore.GetString("PreviousLabel")}
                    </a>
                )}
                {!this.pageNavigation.isLastPageSelected() && (
                    <a
                        onClick={() => this.mutateState(
                            s => s.currentPage = this.pageNavigation.nextPage(),
                            () => {
                                this.updateFormDisplaySelectedPageId(this.pageNavigation.getSelectedPage().id);
                                this.scrollToTop();
                            }
                        )}
                    >
                        {cacheStore.GetString("NextLabel")}
                    </a>
                )}
            </div>
        );
    }

    private handlePageNumberClick(formPage: FormPage): void {
        this.mutateState(
            s => s.currentPage = this.pageNavigation.setSelectedPageById(formPage.id),
            () => this.updateFormDisplaySelectedPageId(formPage.id)
        );
    }

    private scrollToTop(): void {
        const element = document.getElementById(this.getComponentId());
        element.scrollIntoView();
    }

    private updateFormDisplaySelectedPageId(selectedPageId: string): void {
        this.props.onFormDisplayChange(
            this.props.formDisplay
                .setSelectedPageId(selectedPageId)
                .extend()
        );
    }

    private async updateFormWithThrottling(avoidThrottling: boolean = false): Promise<void> {
        if (avoidThrottling) {
            await this.updateForm(this.state.form);
            this.removeThrottleUpdateForm();
        }
        else if (!this.throttleFunc) // form update not already pending
            this.setThrottleUpdateForm(async () => await this.updateForm(this.state.form));
    }

    private async updateForm(form: TForm, obstetricalCounters?: ObstetricalCounters, emrTemplateId?: string, updateState: boolean = true, forceUpdate: boolean = false): Promise<void> {
        if (IsNullOrWhiteSpace(form.Context?.EncounterId)) {
            sessionStore.showPopup("ObstetricForm save error");
            return;
        }

        const formData = form.stringify();

        if (this.state.formSnapshot === formData || (this.props.isReadOnly && !forceUpdate))
            return; // avoid sending the same data to the server

        if (updateState)
            await this.mutateStateAsync(s => s.formSnapshot = formData);

        const response = await this.action.updateForm<TForm>(form.Context.EncounterId, form.Context.ContributionId, form, obstetricalCounters, emrTemplateId);

        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.UpdateObstetricalFileForm,
            {
                form: formData,
                obstetricalCounters: obstetricalCounters,
                modificationDate: response.ModificationDate,
                emrTemplateId: emrTemplateId,
                patientId: form.Context.PatientContactFacetId
            }
        );
    }

    private async updateVitalSign(obstetricalFileVitalSign: ObstetricalFileVitalSign, emrTemplateId: string): Promise<MedicalItemVitalSign> {
        const vitalSignToRequest = obstetricalFileVitalSign?.toMedicalItemVitalSignRequest(this.props.encounter.Id, this.props.encounter.ConsultationDate);

        if (!vitalSignToRequest) {
            this.mutateState(s => s.isVitalSignSaving = false);
            return null;
        }

        if (
            !obstetricalFileVitalSign.hasValue()
            && obstetricalFileVitalSign.isUndelyingMedicalItemEmpty()
            && this.props.obstetricalFileMedicalSummary.isUniqueVitalSignForEmrTemplate(emrTemplateId)
        ) {
            await this.deleteVitalSign(vitalSignToRequest, obstetricalFileVitalSign.getLatestVitalSignMedicalItemId());
            return null;
        }

        return await this.saveVitalSign(vitalSignToRequest, obstetricalFileVitalSign.getLatestVitalSignMedicalItemId());
    }

    private async saveVitalSign(vitalSign: MedicalItemVitalSign, latestMedicalItemId: string): Promise<MedicalItemVitalSign> {
        const action = new ObstetricalFileAction(err => this.handleVitalSignError(err));
        const response = await action.saveVitalSign(
            vitalSign,
            this.props.patient.InactivableResponse.Id,
            this.props.patient.IndividualResponse.Contact_id,
            latestMedicalItemId
        );

        this.mutateState(
            s => s.isVitalSignSaving = false,
            () =>
                sessionStore.dispatch(
                    LocalPatientOperations.MedicalSummary.UpsertVitalSign,
                    {
                        vitalSigns: response.MedicalItems
                    }
                )
        );

        return response.MedicalItems?.firstOrDefault(
            p =>
                p.RelatedEncounterId === this.props.encounter.Id &&
                p.EMRTemplateId === response.EMRTemplateId
        );
    }

    private async deleteVitalSign(vitalSign: MedicalItemVitalSign, latestMedicalItemId: string): Promise<void> {
        const action = new ObstetricalFileAction(err => this.handleVitalSignError(err));

        await action.removeVitalSign(
            vitalSign.Id,
            this.props.encounter.Id,
            latestMedicalItemId
        );

        this.mutateState(s => s.isVitalSignSaving = false, () => {
            sessionStore.dispatch(
                LocalPatientOperations.MedicalSummary.UpsertVitalSign,
                {
                    vitalSigns: this.props.obstetricalFileMedicalSummary.getVitalSignsExcept(vitalSign)
                }
            );
        });
    }

    private handleVitalSignError(errors: ErrorMessage[]): void {
        this.mutateState(
            s => s.isVitalSignSaving = false,
            () => sessionStore.showPopup(errors)
        );
    }

    private handleOnPrint(): void {
        let exportableFile = this.initFileTypeObstetricalFile();

        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning && !sessionStore.getCurrentEmployee().Model.SignatureByteArray) {
            exportableFile.print();
            return;
        }

        this.dispatchFaxOrPrint(exportableFile, ExportMode.Print);
    }

    private dispatchFaxOrPrint(exportableFile: FileTypeObstetricalFile, mode: ExportMode): void {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.FaxPrintExportableFile, {
            config: {
                sentFaxItemId: this.props.contributionId,
                exportableFile: exportableFile,
                exportMode: mode,
                patientContactId: this.props.patient.IndividualResponse.Contact_id,
                onApplyOverlaysCallback: () =>
                    patientStore.updateContributorsEncountersData(this.props.patient.IndividualResponse?.Contact_id, this.props.encounter.Id),
                isSyncToPortalAfterFaxEnabled: this.props.patient.IsRegisteredToPortal() && (this.props.model.hasBeenModifiedSinceLastShared() || this.props.model.SharedItemId == null),
                syncToPortalAfterAction: currentPreview => this.handleOnPortalSync(currentPreview, true),
                selectionPages: this.getSelectionPage()
            } as IFaxPrintExportableFileConfig
        });
    }

    private handleOnPortalSync(exportableFile: IExportableFile = null, skipSignature: boolean = false): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.ObstetricalFile_PortalClick, {
            portalShareableItem: this.props.model,
            exportableFilePreview: exportableFile,
            skipSignature: skipSignature
        });
    }

    private initFileTypeObstetricalFile(): FileTypeObstetricalFile {
        return new FileTypeObstetricalFile({
            contributionId: this.props.model.ContributionId,
            PageNumbers: this.getPageNumbers(),
            description: cacheStore.GetString("ObstetricalFileLabel"),
            date: DateTime.fromJSDate(this.props.model.ContributionCreationDate).toFormat("yyyy-MM-dd")
        });
    }

    private async setThrottleUpdateForm(updateForm: () => Promise<void>): Promise<void> {
        const executionTime = Date.now();
        const differTimeSeconds = (executionTime - this.lastTimeHandleFormUpdateExecution) / 1000;

        if (this.lastTimeHandleFormUpdateExecution == null || differTimeSeconds > this.handleFormUpdateThrottlingSeconds) {
            await updateForm();
            this.lastTimeHandleFormUpdateExecution = executionTime;
            return;
        }

        if (!this.throttleFunc) {
            this.throttleFunc = window.setTimeout(async () => {
                try {
                    await updateForm();
                }
                catch (e) {
                    console.error("Failed to update obstetrical form.", e);
                }

                this.lastTimeHandleFormUpdateExecution = Date.now();
                this.throttleFunc = null;
            }, this.handleFormUpdateThrottlingSeconds * 1000);
        }
    }

    private removeThrottleUpdateForm(): void {
        this.lastTimeHandleFormUpdateExecution = Date.now();
        clearTimeout(this.throttleFunc);
        this.throttleFunc = null;
    }
}

export { IObstetricalFileProps, ObstetricalFile };
